import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { resilientFetch } from '../lib/resilientFetch';
import MoleculeLoader from '../components/MoleculeLoader';
import { HeroVials } from '../components/HeroVials';
import { 
  FlaskConical, 
  Shield, 
  Award, 
  Microscope,
  Activity,
  Flame,
  Brain,
  Dna,
  ArrowRight,
  CheckCircle2,
  FileText,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format image URLs (handle local uploads vs external URLs)
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/uploads') || url.startsWith('/api/uploads')) return `${API_URL}${url}`;
  return url;
};

const HERO_BG = 'https://images.unsplash.com/photo-1726436467696-9586b2339cc5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRuYSUyMHN0cnVjdHVyZSUyMG1vbGVjdWxhciUyMGJpb2xvZ3klMjB0ZWFsJTIwZ3JlZW58ZW58MHx8fHwxNzcwNzAwNzg4fDA&ixlib=rb-4.1.0&q=85';
const QUALITY_IMG = 'https://images.unsplash.com/photo-1605781645799-c9c7d820b4ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzY2llbmNlJTIwbGFib3JhdG9yeSUyMGNsZWFuJTIwd2hpdGUlMjBtaW5pbWFsfGVufDB8fHx8MTc3MDcwMDc5Mnww&ixlib=rb-4.1.0&q=85';

// Hero vials composite image
const HERO_VIALS_IMAGE = 'https://customer-assets.emergentagent.com/job_a3314fd0-818c-4089-8369-a8d30cc8683e/artifacts/o1vl4m0m_image.png';

const categoryIcons = {
  'recovery-repair': Activity,
  'metabolic': Flame,
  'cognitive': Brain,
  'longevity': Dna,
};

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Newsletter signup
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterDone, setNewsletterDone] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const email = newsletterEmail.trim();
    if (!email) return;
    setNewsletterLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'homepage' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.detail || 'Could not subscribe right now. Please try again.');
        return;
      }
      toast.success(data.new_subscriber ? 'Thanks — welcome aboard!' : 'You\'re already subscribed.');
      setNewsletterDone(true);
      setNewsletterEmail('');
    } catch (err) {
      toast.error('Network error — please try again.');
    } finally {
      setNewsletterLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let intervalId = 0;
    let attempts = 0;

    const fetchData = async () => {
      const [productsRes, categoriesRes, blogRes] = await Promise.all([
        resilientFetch(`${API_URL}/api/products?featured=true`),
        resilientFetch(`${API_URL}/api/categories`),
        resilientFetch(`${API_URL}/api/blog`),
      ]);
      if (cancelled) return;
      if (productsRes.ok) setFeaturedProducts(productsRes.data || []);
      if (categoriesRes.ok) setCategories(categoriesRes.data || []);
      if (blogRes.ok) setBlogPosts((blogRes.data || []).slice(0, 3));
      setLoading(false);

      // If the featured products call failed, schedule silent background
      // retries every 4s (max 6) so the homepage self-heals after a deploy
      // cold-start without the customer having to refresh.
      if (!productsRes.ok && !intervalId) {
        intervalId = window.setInterval(() => {
          attempts += 1;
          if (attempts > 6) {
            window.clearInterval(intervalId);
            intervalId = 0;
            return;
          }
          fetchData();
        }, 4000);
      } else if (productsRes.ok && intervalId) {
        window.clearInterval(intervalId);
        intervalId = 0;
      }
    };

    fetchData();

    // Re-fetch immediately when the browser regains network connectivity.
    const onOnline = () => fetchData();
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <SEO 
        title="Premium Research-Grade Peptides"
        description="Genora Peptides offers research-grade peptides synthesized to ≥99% purity. Third-party tested with full Certificates of Analysis. Shop AOD-9604, BPC-157, TB-500 and more."
        keywords="research peptides, AOD-9604, BPC-157, TB-500, laboratory peptides, high purity peptides, peptide research"
      />
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
        {/* Background */}
        <div className="absolute inset-0">
          <img 
            src={HERO_BG} 
            alt="" 
            className="w-full h-full object-cover opacity-5"
          />
        </div>

        {/* Gradient overlay for smooth blend */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 via-50% to-transparent"></div>

        {/* Vials Image - Right Side with fade blend */}
        <div className="absolute right-0 top-0 bottom-0 hidden lg:flex items-center w-[58%] xl:w-[55%] -ml-12 z-0">
          <div className="relative w-full h-full flex items-center">
            {/* Left fade gradient over the image */}
            <div className="absolute left-0 top-0 bottom-0 w-40 bg-gradient-to-r from-white to-transparent z-10"></div>
            <HeroVials videoSrc="/hero-vials.mp4" posterSrc={HERO_VIALS_IMAGE} />
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 w-full">
          <div className="max-w-xl lg:max-w-md xl:max-w-lg">
            {/* Disclaimer Badge */}
            <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 border border-amber-200 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider mb-8 animate-fade-in">
              <Shield className="h-4 w-4" />
              For Research Purposes Only
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6 animate-slide-up">
              Precision Peptides for{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-800 to-teal-600">
                Advanced Research
              </span>
            </h1>

            <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-2xl animate-slide-up stagger-1">
              Research-grade peptides synthesized to ≥99% purity. Third-party tested with full 
              Certificates of Analysis available for every batch. Trusted by laboratories worldwide.
            </p>

            <div className="flex flex-wrap gap-4 animate-slide-up stagger-2">
              <Link to="/products" data-testid="hero-view-products">
                <Button size="lg" className="bg-teal-700 hover:bg-teal-800 text-white h-12 px-8 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
                  View Products
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/coas" data-testid="hero-view-coas">
                <Button variant="outline" size="lg" className="border-2 border-teal-700 text-teal-700 hover:bg-teal-50 h-12 px-8 rounded-full font-semibold transition-all">
                  <FileText className="mr-2 h-5 w-5" />
                  Lab Testing & COAs
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Microscope, title: 'Third-Party Tested', desc: 'Independent lab verification' },
              { icon: Award, title: 'High Purity ≥99%', desc: 'HPLC verified standards' },
              { icon: Shield, title: 'Made in the USA', desc: 'Domestic synthesis' },
              { icon: FlaskConical, title: 'Research-Grade', desc: 'Laboratory quality assured' },
            ].map((badge, index) => (
              <Card key={index} className="bg-white border-slate-100 hover:shadow-md transition-shadow" data-testid={`trust-badge-${index}`}>
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-50 text-teal-700 mb-4">
                    <badge.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-heading font-semibold text-slate-900 mb-1">{badge.title}</h3>
                  <p className="text-sm text-slate-500">{badge.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Research Categories
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Explore our comprehensive catalog of research peptides organized by application area.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category) => {
              const IconComponent = categoryIcons[category.id] || FlaskConical;
              return (
                <Link 
                  key={category.id} 
                  to={`/products?category=${category.id}`}
                  data-testid={`category-${category.id}`}
                >
                  <Card className="group h-full bg-white border-slate-100 hover:border-teal-200 hover:shadow-lg transition-all duration-300 cursor-pointer">
                    <CardContent className="p-6">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-teal-50 text-teal-700 mb-4 group-hover:bg-teal-100 transition-colors">
                        <IconComponent className="h-7 w-7" />
                      </div>
                      <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2 group-hover:text-teal-700 transition-colors">
                        {category.name}
                      </h3>
                      <p className="text-sm text-slate-500">{category.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
                Featured Products
              </h2>
              <p className="text-slate-600">Most popular research peptides</p>
            </div>
            <Link to="/products" data-testid="view-all-products">
              <Button variant="outline" className="border-teal-700 text-teal-700 hover:bg-teal-50">
                View All Products
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="relative">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                    <div className="aspect-square bg-slate-200 rounded-xl mb-4"></div>
                    <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-sm">
                  <MoleculeLoader size={72} label="Loading featured peptides…" />
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => {
                const hasVariants = product.variants && product.variants.length > 0;
                const startingPrice = hasVariants 
                  ? Math.min(...product.variants.map(v => v.price))
                  : product.price;
                const displaySize = hasVariants 
                  ? `${product.variants.length} sizes`
                  : product.vial_size;
                
                return (
                  <Link key={product.product_id} to={`/products/${product.slug}`} data-testid={`product-${product.slug}`}>
                    <Card className="group bg-white border-slate-100 overflow-hidden hover:shadow-lg transition-all duration-300">
                      <div className="aspect-square p-4 bg-slate-50">
                        <img 
                          src={getImageUrl(product.image_url)} 
                          alt={product.name}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <CardContent className="p-5">
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200 mb-3 text-[10px]">
                          Research Use Only
                        </Badge>
                        <h3 className="font-heading font-semibold text-slate-900 mb-1 group-hover:text-teal-700 transition-colors">
                          {product.name}
                        </h3>
                        <p className="text-sm text-slate-500 mb-3">{displaySize} • {product.purity}</p>
                        <div className="flex items-center justify-between">
                          <span className="font-heading font-bold text-lg text-teal-700">
                            {hasVariants ? `From $${startingPrice?.toFixed(2)}` : `$${startingPrice?.toFixed(2)}`}
                          </span>
                          <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white">
                            View Details
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Quality & Testing Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-teal-50 text-teal-700 border-teal-200 mb-4">Quality Assurance</Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Rigorous Testing Standards
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Every batch of Genora Peptides undergoes comprehensive third-party testing to ensure 
                research-grade quality. Our commitment to transparency means full documentation for 
                every product we offer.
              </p>

              <ul className="space-y-4 mb-8">
                {[
                  'HPLC purity analysis for every batch',
                  'Mass spectrometry identity verification',
                  'Comprehensive Certificate of Analysis',
                  'Batch tracking and documentation',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>

              <Link to="/coas" data-testid="quality-view-coas">
                <Button className="bg-teal-700 hover:bg-teal-800 text-white">
                  View Certificates of Analysis
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img 
                src={QUALITY_IMG} 
                alt="Laboratory Testing"
                className="w-full rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 max-w-[200px]">
                <div className="text-3xl font-heading font-bold text-teal-700 mb-1">≥99%</div>
                <div className="text-sm text-slate-600">Verified Purity</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Insights / Blog Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
                Research Insights
              </h2>
              <p className="text-slate-600">Educational resources for laboratory researchers</p>
            </div>
            <Link to="/research-insights" data-testid="view-all-insights">
              <Button variant="outline" className="border-teal-700 text-teal-700 hover:bg-teal-50">
                View All Articles
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {blogPosts.map((post) => (
              <Link key={post.post_id} to={`/research-insights/${post.slug}`} data-testid={`blog-${post.slug}`}>
                <Card className="group h-full bg-white border-slate-100 overflow-hidden hover:shadow-lg transition-all duration-300">
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={post.image_url} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="p-5">
                    <p className="text-xs text-slate-500 mb-2">{new Date(post.published_at).toLocaleDateString()}</p>
                    <h3 className="font-heading font-semibold text-slate-900 mb-2 group-hover:text-teal-700 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-slate-500 line-clamp-2">{post.excerpt}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Sign-up */}
      <section className="py-16 bg-gradient-to-br from-teal-700 to-teal-800" data-testid="newsletter-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center text-white">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 mb-4">
            <Mail className="h-6 w-6 text-white" />
          </div>
          <h2 className="font-heading text-3xl font-bold mb-3">Research updates, straight to your inbox</h2>
          <p className="text-teal-50 mb-8 max-w-xl mx-auto">
            New peptide releases, research articles, and member-only discounts. No spam — and you can opt out in one click any time.
          </p>
          {newsletterDone ? (
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/20 px-5 py-3 rounded-full" data-testid="newsletter-thanks">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Thanks! You're on the list.</span>
            </div>
          ) : (
            <form
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
              data-testid="newsletter-form"
            >
              <label htmlFor="newsletter-email" className="sr-only">Email address</label>
              <Input
                id="newsletter-email"
                type="email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="you@lab.com"
                className="bg-white text-slate-900 border-0 placeholder:text-slate-400"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                disabled={newsletterLoading}
                className="bg-white text-teal-700 hover:bg-teal-50 disabled:opacity-60"
                data-testid="newsletter-submit-btn"
              >
                {newsletterLoading ? 'Subscribing…' : 'Subscribe'}
              </Button>
            </form>
          )}
          <p className="text-xs text-teal-100/80 mt-4">
            By subscribing you agree to receive marketing and research emails. Review our <Link to="/terms" className="underline hover:text-white">Terms & Communication Preferences</Link>.
          </p>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-bold text-white mb-4">
              Compliance & Legal Notice
            </h2>
            <div className="max-w-3xl mx-auto">
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                All products sold by Genora Peptides are intended for laboratory research use only. 
                They are not intended for human or animal consumption. Products are not drugs and have 
                not been approved by the FDA to diagnose, treat, cure, or prevent any disease or medical condition.
              </p>
              <div className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-400 border border-amber-500/30 px-4 py-2 rounded-full text-sm font-medium">
                <Shield className="h-4 w-4" />
                For Research Purposes Only — Not for Human Consumption
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
